/**
 * Tabs — the WAI-ARIA tabs pattern, once.
 *
 * Four `role="tablist"`s shipped with ZERO `role="tabpanel"`s, zero `aria-controls`, no
 * roving `tabIndex` and no arrow keys, and a fifth tab-shaped group (Admin) used
 * `aria-pressed` on a `<nav>` — identical visuals, different semantics (AUD-2 IM-7 / IM-8,
 * F-39). A screen reader was told "tab 3 of 6, selected" and then given nothing to move into.
 *
 * The contract, asserted by `tabs.test.tsx`:
 *   1. `role="tablist"` whose ONLY element children are `role="tab"` — no wrapper span. That
 *      is why the hint tooltip uses `useTooltipTrigger` rather than `<Tooltip>`, and why the
 *      hook's `bubble` is a PORTAL onto `document.body`: written here it looks like a sibling
 *      of the tab, and as a real element it WAS one, so the tablist grew a `<span
 *      role="tooltip">` child for as long as a tab was hovered or focused. Rule 1 is asserted
 *      in those two states (`tabs.test.tsx` 1b, `ui/tooltip.test.tsx`), not only at rest.
 *   2. Each tab: `id={idPrefix}-tab-{key}`, `aria-selected`, `aria-controls` pointing at the
 *      panel, and roving `tabIndex` (0 on the active tab, -1 on the rest) so Tab enters the
 *      tablist once and the arrows move within it.
 *   3. One `role="tabpanel"`, `id={idPrefix}-panel-{active}`, `aria-labelledby` the active
 *      tab, `tabIndex={0}` so a keyboard user can reach content that has no focusable child.
 *   4. ArrowLeft / ArrowRight move selection AND focus, wrapping; Home / End jump to first /
 *      last. Up/Down are NOT bound — this is a horizontal tablist, and binding them would
 *      shadow the browser's own scrolling.
 *   5. Existing `data-testid`s survive via `TabDef.testId`, defaulting to
 *      `{idPrefix}-tab-{key}` — exactly what the call sites already render, so every jsdom
 *      test and every probe selector keeps working.
 *
 * `.workspace__tab--active` is still applied on the selected tab: WS-K1's design-system
 * test matches theme.css's literal selector pair to prove the focus-ring erasure trap stayed
 * gone, and that pair names this class.
 */
import { useRef, type ReactNode } from "react";
import { useTooltipTrigger } from "./Tooltip.js";

export type TabDef = {
  key: string;
  label: string;
  /** Optional one-line hint, shown as a tooltip on hover/focus. */
  hint?: string;
  /** Overrides the default `{idPrefix}-tab-{key}` testid. */
  testId?: string;
};

function Tab({
  def,
  active,
  idPrefix,
  onSelect,
  onKeyDown,
  tabRef,
}: {
  def: TabDef;
  active: boolean;
  idPrefix: string;
  onSelect: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
  tabRef: (el: HTMLButtonElement | null) => void;
}) {
  const tip = useTooltipTrigger(def.hint);
  return (
    <>
      <button
        type="button"
        role="tab"
        id={`${idPrefix}-tab-${def.key}`}
        aria-selected={active}
        aria-controls={`${idPrefix}-panel-${def.key}`}
        tabIndex={active ? 0 : -1}
        ref={(el) => {
          tip.ref.current = el;
          tabRef(el);
        }}
        className={`workspace__tab${active ? " workspace__tab--active" : ""}`}
        onClick={onSelect}
        onKeyDown={onKeyDown}
        data-testid={def.testId ?? `${idPrefix}-tab-${def.key}`}
        {...tip.triggerProps}
      >
        {def.label}
      </button>
      {tip.bubble}
    </>
  );
}

export function Tabs({
  tabs,
  active,
  onChange,
  label,
  idPrefix,
  className = "workspace__tabs",
  panelClassName = "workspace__body",
  children,
}: {
  tabs: TabDef[];
  active: string;
  onChange: (key: string) => void;
  /** `aria-label` for the tablist — what this set of tabs switches between. */
  label: string;
  /** Namespaces the generated ids and the default testids, e.g. "editor" → editor-tab-options. */
  idPrefix: string;
  className?: string;
  panelClassName?: string;
  /** Rendered inside the single tabpanel. */
  children: ReactNode;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const move = (to: number) => {
    const next = tabs[(to + tabs.length) % tabs.length];
    if (!next) return;
    onChange(next.key);
    // Selection AND focus, per the pattern. jsdom cannot prove focus moved; the measured
    // half of design-gate.mjs drives this in a real browser.
    refs.current[(to + tabs.length) % tabs.length]?.focus();
  };

  const onKeyDown = (i: number) => (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowRight") move(i + 1);
    else if (e.key === "ArrowLeft") move(i - 1);
    else if (e.key === "Home") move(0);
    else if (e.key === "End") move(tabs.length - 1);
    else return;
    e.preventDefault();
  };

  const activeTab = tabs.find((t) => t.key === active) ?? tabs[0];

  return (
    <>
      <div className={className} role="tablist" aria-label={label}>
        {tabs.map((t, i) => (
          <Tab
            key={t.key}
            def={t}
            active={t.key === active}
            idPrefix={idPrefix}
            onSelect={() => onChange(t.key)}
            onKeyDown={onKeyDown(i)}
            tabRef={(el) => {
              refs.current[i] = el;
            }}
          />
        ))}
      </div>
      <div
        className={panelClassName}
        role="tabpanel"
        id={`${idPrefix}-panel-${activeTab?.key ?? active}`}
        aria-labelledby={`${idPrefix}-tab-${activeTab?.key ?? active}`}
        tabIndex={0}
      >
        {children}
      </div>
    </>
  );
}
